import { cn } from "@/lib/utils"

/**
 * The printed label's wording beside the approved wording.
 *
 * One component for every surface of the report that shows a divergence — the
 * checklist card now, the list and document views next — so a client sees one
 * comparison in every place, always in the same order and colours: what is
 * printed first, what was approved second.
 *
 * Either side can legitimately be absent ("the master requires this and the
 * label omits it" is one of the commonest findings), so a missing side says so
 * rather than rendering an empty box.
 */
export function DivergencePair({
  printedText,
  masterText,
  fallback,
  stacked = false,
  className,
}: {
  printedText: string | null
  masterText: string | null
  /** The narrated form, shown only when neither side was captured separately. */
  fallback?: string | null
  /** Stack the boxes rather than set them side by side — for narrow panes. */
  stacked?: boolean
  className?: string
}): JSX.Element | null {
  if (!printedText && !masterText) {
    if (!fallback) return null
    return (
      <div
        className={cn(
          "rounded-sm border border-border bg-surface-sunken p-2.5 text-caption",
          className,
        )}
      >
        <span className="block font-semibold text-text-2">Divergence</span>
        <span className="break-words text-text-2">{fallback}</span>
      </div>
    )
  }

  return (
    <div className={cn("grid gap-2", stacked ? "grid-cols-1" : "sm:grid-cols-2", className)}>
      <Side
        label="Printed label"
        text={printedText}
        absent="Not present on the printed label."
        boxClassName="border-err/30 bg-err-tint"
        labelClassName="text-err"
      />
      <Side
        label="EPA master"
        text={masterText}
        absent="Not present on the approved master."
        boxClassName="border-master/30 bg-master-tint"
        labelClassName="text-master"
      />
    </div>
  )
}

function Side({
  label,
  text,
  absent,
  boxClassName,
  labelClassName,
}: {
  label: string
  text: string | null
  absent: string
  boxClassName: string
  labelClassName: string
}): JSX.Element {
  return (
    <div className={cn("rounded-sm border p-2.5 text-caption", boxClassName)}>
      <span className={cn("block font-semibold", labelClassName)}>{label}</span>
      {text ? (
        <span className="break-words text-text-2">{text}</span>
      ) : (
        <span className="italic text-text-3">{absent}</span>
      )}
    </div>
  )
}
